import { useState, useEffect, useCallback } from 'react';
import { listAdvances, addAdvance, decideAdvance, removeAdvance, requestAdvance, markAdvancePaid } from '../lib/advances';

const money = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const field = 'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-300';
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const STATUS = {
  pending: ['Pendiente', 'bg-amber-100 text-amber-700'],
  approved: ['Aprobado · falta pagar', 'bg-emerald-100 text-emerald-700'],
  paid: ['Pagado', 'bg-teal-100 text-teal-700'],
  rejected: ['Rechazado', 'bg-red-100 text-red-600'],
};
const Pill = ({ s }) => <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${STATUS[s][1]}`}>{STATUS[s][0]}</span>;

// Administrador: ver, aprobar y cargar adelantos de sueldo del mes.
export function AdminAdvances({ employees, month, adminId, showToast, onChange, canEdit = true }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ employeeId: '', amount: '', paidOn: todayISO(), note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await listAdvances({ month })); } catch (e) { showToast?.(e.message, 'error'); }
  }, [month, showToast]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); showToast?.(ok); await load(); onChange?.(); } catch (e) { showToast?.(e.message, 'error'); }
    setBusy(false);
  };
  const add = () => {
    if (!form.employeeId || !(Number(form.amount) > 0)) return showToast?.('Elegí la persona e ingresá un monto', 'error');
    run(async () => {
      await addAdvance({ ...form, month, adminId });
      setForm({ employeeId: '', amount: '', paidOn: todayISO(), note: '' });
    }, 'Adelanto registrado');
  };

  const pend = rows.filter(r => r.status === 'pending').length;
  const total = rows.filter(r => r.status === 'approved' || r.status === 'paid').reduce((a, r) => a + Number(r.amount), 0);

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="px-4 py-2.5 rounded-2xl text-sm font-bold border border-sky-200 text-sky-700 bg-white hover:bg-sky-50">
        Adelantos{pend > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pend}</span>}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Adelantos de sueldo · {month}</h3>
                <p className="text-xs text-gray-400">Los aprobados se descuentan del sueldo a liquidar de este mes. Total a descontar: <b>{money(total)}</b></p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 text-xl leading-none">×</button>
            </div>

            {canEdit && (
              <div className="rounded-2xl border border-gray-100 p-3.5 space-y-2.5 bg-gray-50/50">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Registrar adelanto ya entregado</p>
                <select className={field} value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })}>
                  <option value="">Elegí la persona…</option>
                  {employees.filter(e => e.role === 'employee').map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <input className={field} type="number" min="1" placeholder="Monto ($)" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                  <input className={field} type="date" value={form.paidOn} onChange={e => setForm({ ...form, paidOn: e.target.value })} />
                </div>
                <input className={field} placeholder="Nota (opcional)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
                <button disabled={busy} onClick={add} className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Registrar adelanto</button>
              </div>
            )}

            {rows.length === 0 ? <p className="text-sm text-gray-400 text-center py-6">Sin adelantos este mes</p> : (
              <div className="divide-y divide-gray-50">
                {rows.map(r => (
                  <div key={r.id} className="py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900">{r.employee?.name || '—'} <span className="text-emerald-600">{money(r.amount)}</span></p>
                      <p className="text-xs text-gray-400">{r.paid_on}{r.requested_by_employee ? ' · pedido por el empleado' : ' · cargado por la administración'}{r.note ? ` · ${r.note}` : ''}</p>
                    </div>
                    <Pill s={r.status} />
                    {canEdit && (
                      <div className="flex gap-1">
                        {r.status === 'pending' && <>
                          <button disabled={busy} onClick={() => run(() => decideAdvance(r.id, 'approved', adminId), 'Adelanto aprobado')} className="px-2 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-lg">Aprobar</button>
                          <button disabled={busy} onClick={() => run(() => decideAdvance(r.id, 'rejected', adminId), 'Adelanto rechazado')} className="px-2 py-1 text-xs font-bold text-red-600 bg-red-50 rounded-lg">Rechazar</button>
                        </>}
                        {r.status === 'approved' && (
                          <button disabled={busy} onClick={() => run(() => markAdvancePaid(r.id, adminId), 'Adelanto marcado como pagado')} className="px-2 py-1 text-xs font-bold text-teal-700 bg-teal-50 rounded-lg">Marcar pagado</button>
                        )}
                        {r.status !== 'pending' && (
                          <button disabled={busy} onClick={() => window.confirm('¿Eliminar este adelanto?') && run(() => removeAdvance(r.id), 'Adelanto eliminado')} className="px-2 py-1 text-xs text-gray-400 hover:text-red-500">Eliminar</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Empleado: pide un adelanto y ve el estado de los suyos.
export function EmployeeAdvances({ employeeId, month }) {
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setRows(await listAdvances({ employeeId })); } catch { /* sin acceso */ }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!(Number(amount) > 0)) return setMsg('Ingresá un monto válido');
    setBusy(true); setMsg('');
    try { await requestAdvance({ employeeId, amount, month, note }); setAmount(''); setNote(''); setMsg('Pedido enviado. Te avisamos cuando se decida.'); await load(); }
    catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-5 space-y-3">
      <div>
        <h3 className="font-bold text-gray-900 text-sm">Adelantos de sueldo</h3>
        <p className="text-xs text-gray-400">Pedí un adelanto: si se aprueba, se descuenta de tu sueldo de {month}.</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className={field} type="number" min="1" placeholder="Monto ($)" value={amount} onChange={e => setAmount(e.target.value)} />
        <input className={field} placeholder="Motivo (opcional)" value={note} onChange={e => setNote(e.target.value)} />
      </div>
      <button disabled={busy} onClick={send} className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Pedir adelanto</button>
      {msg && <p className="text-xs text-gray-500">{msg}</p>}
      {rows.length > 0 && (
        <div className="divide-y divide-gray-50">
          {rows.slice(0, 8).map(r => (
            <div key={r.id} className="py-2.5 flex items-center justify-between gap-2">
              <div><p className="text-sm font-bold text-gray-800">{money(r.amount)}</p><p className="text-xs text-gray-400">{r.paid_on} · sueldo de {r.month}</p></div>
              <Pill s={r.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
